import React, { useState } from 'react';
import { Card, Table, Button, Badge, Form, Modal, Spinner } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { useHistory, useLocation } from 'react-router-dom';
import Pagination from 'react-js-pagination';
import { CME_DIEU_DUONG_API, ACCOUNTINFO_API, BaseGatewayImage, ACCOUNT_API, ACCOUNT_AUTHINFO_API } from '../../../../utils/apiUrl';
import { Get, Post } from '../../../../utils/httpProvider';
import UserInfoCell from '../duyetHoSo/UserInfoCell';
import CMEParticipantCell from '../duyetHoSo/CMEParticipantCell';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';
import moment from 'moment';
import { objectShowNumber } from '../../../../utils/define';
import { createUrlWithParam } from '../../../../utils/helper';

// Helper component to display Unit/Department (Hybrid: app_dungchung first, local fallback)
const UserUnitCell = ({ userId }) => {
    const { data: sharedInfo } = useQuery(
        ['sharedAccountInfoUnit', userId],
        () => Get(ACCOUNT_API.GET_ACCOUNT_INFO_BYID_FROM_APP_DUNGCHUNG, userId),
        { enabled: !!userId, staleTime: 600000, cacheTime: 900000 }
    );

    const donVis = sharedInfo?.donVis || sharedInfo?.DonVis?.$values || sharedInfo?.DonVis || [];
    const maDonVi = (Array.isArray(donVis) && donVis.length > 0)
        ? donVis.map(d => d.maDonVi || d.MaDonVi || d.tenDonVi || d.TenDonVi).filter(Boolean).join(', ')
        : '-';

    return <span>{maDonVi}</span>;
};

const CMEDieuDuongDuyet = () => {
    const queryClient = useQueryClient();
    const history = useHistory();
    const location = useLocation();

    // Parse URL search params
    const paramUrl = new URLSearchParams(location.search);
    const initialPage = paramUrl.get('page') != null ? parseInt(paramUrl.get('page'), 10) : 1;
    const initialPageSize = paramUrl.get('size') != null ? parseInt(paramUrl.get('size'), 10) : 20;
    const initialKeyword = paramUrl.get('search') != null ? paramUrl.get('search') : '';
    const initialStatus = paramUrl.get('status') != null ? paramUrl.get('status') : '';

    const [page, setPage] = useState(initialPage);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const [keyword, setKeyword] = useState(initialKeyword);
    const [statusFilter, setStatusFilter] = useState(initialStatus);

    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [openApproveModal, setOpenApproveModal] = useState(false);
    const [openRejectModal, setOpenRejectModal] = useState(false);

    const [detailSelectedIds, setDetailSelectedIds] = useState([]);
    const [lyDoTuChoi, setLyDoTuChoi] = useState('');

    // Modal-specific filter state
    const [modalKeyword, setModalKeyword] = useState('');

    const [unitMap, setUnitMap] = useState({});
    const [userMap, setUserMap] = useState({});

    // Reset states on modal select change
    React.useEffect(() => {
        setModalKeyword('');
    }, [selectedCourseId]);

    // Sync state with URL when URL changes
    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const p = params.get('page') != null ? parseInt(params.get('page'), 10) : 1;
        const s = params.get('size') != null ? parseInt(params.get('size'), 10) : 20;
        const kw = params.get('search') != null ? params.get('search') : '';
        const st = params.get('status') != null ? params.get('status') : '';

        if (p !== page) setPage(p);
        if (s !== pageSize) setPageSize(s);
        if (kw !== keyword) setKeyword(kw);
        if (st !== statusFilter) setStatusFilter(st);
    }, [location.search]);

    const cleanParams = (params) => {
        const result = {};
        Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                result[key] = value;
            }
        });
        return result;
    };

    const replaceHistory = (Page, Size, Search, Status) => {
        const rawParams = {
            page: Page,
            size: Size,
            search: Search || null,
            status: Status || null,
        };
        history.replace(
            createUrlWithParam(location.pathname, cleanParams(rawParams))
        );
    };

    // Ensure default params exist in URL on mount
    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('page') == null || params.get('size') == null || params.get('search') == null) {
            replaceHistory(page, pageSize, keyword, statusFilter);
        }
    }, []);

    const changePage = (number) => {
        replaceHistory(number, pageSize, keyword, statusFilter);
    };

    const onChangeShowNumber = (e) => {
        const val = Number(e.target.value);
        replaceHistory(1, val, keyword, statusFilter);
    };

    const onChangeKeyword = (e) => {
        const val = e.target.value;
        replaceHistory(1, pageSize, val, statusFilter);
    };

    const onChangeStatus = (e) => {
        const val = e.target.value;
        replaceHistory(1, pageSize, keyword, val);
    };

    // Query Step 2 Paging list
    const { data: step2PagingData, isLoading, refetch } = useQuery(
        ['cmeDieuDuongDuyetList', statusFilter, keyword, page, pageSize],
        () => Get(`${CME_DIEU_DUONG_API.GET_STEP2_PAGING}?status=${statusFilter}&keyword=${keyword}&pageIndex=${page}&pageSize=${pageSize}`),
        { keepPreviousData: true }
    );

    const items = step2PagingData?.data?.$values || step2PagingData?.data || step2PagingData?.Data?.$values || step2PagingData?.Data || [];
    const totalRecords = step2PagingData?.totalRecords || step2PagingData?.TotalRecords || 0;

    const approveMutation = useMutation((payload) => Post(CME_DIEU_DUONG_API.APPROVE_STEP2, null, payload));

    // Group records by Course
    const groupedCourses = React.useMemo(() => {
        const groups = {};
        items.forEach(record => {
            const course = record.cmeKhoaDaoTaoDieuDuong;
            if (!course) return;

            if (!groups[course.id]) {
                groups[course.id] = {
                    courseInfo: course,
                    records: [],
                    pendingCount: 0,
                    approvedCount: 0,
                    rejectedCount: 0,
                    minhChung: ''
                };
            }

            groups[course.id].records.push(record);
            if (record.trangThaiDuyet === 1) groups[course.id].pendingCount++;
            else if (record.trangThaiDuyet === 2) groups[course.id].rejectedCount++;
            else if (record.trangThaiDuyet === 3) groups[course.id].approvedCount++;

            if (!groups[course.id].minhChung && record.minhChung) {
                groups[course.id].minhChung = record.minhChung;
            }
        });
        return Object.values(groups);
    }, [items]);

    const activeCourse = React.useMemo(() => {
        return groupedCourses.find(c => c.courseInfo.id === selectedCourseId);
    }, [groupedCourses, selectedCourseId]);

    // Batch load details (names & units) for searching & sorting inside modal
    React.useEffect(() => {
        const fetchDetails = async () => {
            if (!activeCourse || !activeCourse.records || activeCourse.records.length === 0) return;
            const records = activeCourse.records;
            const uniqueUserIds = [...new Set(records.map(x => x.userId))].filter(id => !unitMap[id] || !userMap[id]);
            if (uniqueUserIds.length === 0) return;

            const newUnits = { ...unitMap };
            const newUsers = { ...userMap };

            await Promise.all(uniqueUserIds.map(async (userId) => {
                try {
                    // Dùng AccountAuthInfo/GetItem (quyền thấp hơn Account/GetItem)
                    const accountInfo = await Get(ACCOUNT_AUTHINFO_API.GET_BY_ID_ACCOUNT, userId);
                    queryClient.setQueryData(['accountInfo', userId], accountInfo);
                    
                    // AccountAuthInfo/GetItem trả về fields trực tiếp, fallback về nested nếu cần
                    const authInfo = accountInfo?.accountAuthInfo || accountInfo?.AccountAuthInfo || accountInfo;
                    const fullName = [
                        authInfo?.lastName || authInfo?.LastName,
                        authInfo?.middleName || authInfo?.MiddleName,
                        authInfo?.firstName || authInfo?.FirstName
                    ].filter(Boolean).join(' ') || 'Chưa cập nhật';
                    const username = accountInfo?.username || accountInfo?.Username || '';
                    newUsers[userId] = { fullName, username };

                    // Fetch unit info
                    let donVis = [];
                    try {
                        const sharedInfo = await Get(ACCOUNT_API.GET_ACCOUNT_INFO_BYID_FROM_APP_DUNGCHUNG, userId);
                        let localInfo = null;
                        if (!sharedInfo?.donVis && !sharedInfo?.DonVis) {
                            localInfo = await Get(ACCOUNTINFO_API.GET_BY_ACCOUNT_ID, userId);
                        }
                        donVis = sharedInfo?.donVis || sharedInfo?.DonVis?.$values || sharedInfo?.DonVis || localInfo?.donVis || [];
                    } catch (unitErr) {
                        console.warn("Lỗi khi lấy thông tin đơn vị cho user:", userId, unitErr);
                    }

                    if (donVis && donVis.length > 0) {
                        const unitObj = donVis[0];
                        const code = unitObj.maDonVi || unitObj.MaDonVi || unitObj.tenDonVi || unitObj.TenDonVi || '-';
                        newUnits[userId] = { code };
                    } else {
                        newUnits[userId] = { code: '-' };
                    }
                } catch (e) {
                    newUnits[userId] = { code: '-' };
                    newUsers[userId] = { fullName: 'Chưa cập nhật', username: '' };
                }
            }));
            setUnitMap(newUnits);
            setUserMap(newUsers);
        };
        fetchDetails();
    }, [activeCourse]);

    // Filter & sort records inside modal
    const filteredModalRecords = React.useMemo(() => {
        if (!activeCourse || !activeCourse.records) return [];
        let result = [...activeCourse.records];

        if (modalKeyword.trim()) {
            const kw = modalKeyword.trim().toLowerCase();
            result = result.filter(item => {
                const user = userMap[item.userId];
                const unit = unitMap[item.userId];
                const fullName = user?.fullName?.toLowerCase() || '';
                const username = user?.username?.toLowerCase() || '';
                const unitCode = unit?.code?.toLowerCase() || '';
                return fullName.includes(kw) || username.includes(kw) || unitCode.includes(kw);
            });
        }

        // Sort by unit code (MaDonVi)
        return result.sort((a, b) => {
            const unitA = unitMap[a.userId];
            const unitB = unitMap[b.userId];
            const codeA = unitA ? unitA.code : '';
            const codeB = unitB ? unitB.code : '';
            return codeA.localeCompare(codeB, 'vi');
        });
    }, [activeCourse, userMap, unitMap, modalKeyword]);



    React.useEffect(() => {
        if (activeCourse) {
            setDetailSelectedIds(activeCourse.records.filter(x => x.trangThaiDuyet === 3).map(x => x.id));
        } else {
            setDetailSelectedIds([]);
        }
    }, [activeCourse]);

    const handleSelectAllDetail = (checked) => {
        const activeIds = filteredModalRecords.filter(x => x.trangThaiDuyet === 1).map(x => x.id);
        const lockedApprovedIds = activeCourse?.records.filter(x => x.trangThaiDuyet === 3).map(x => x.id) || [];
        if (checked) {
            setDetailSelectedIds(prev => [...new Set([...prev, ...activeIds, ...lockedApprovedIds])]);
        } else {
            setDetailSelectedIds(prev => prev.filter(id => !activeIds.includes(id) || lockedApprovedIds.includes(id)));
        }
    };

    const handleSelectOneDetail = (id, checked) => {
        const item = activeCourse?.records.find(x => x.id === id);
        if (item && item.trangThaiDuyet !== 1) {
            return; // Locked item cannot be modified
        }
        if (checked) {
            setDetailSelectedIds(prev => [...prev, id]);
        } else {
            setDetailSelectedIds(prev => prev.filter(x => x !== id));
        }
    };

    const confirmApprove = () => {
        // Filter out status 3 ones since they are already approved on backend
        const recordsToApprove = detailSelectedIds.filter(id => {
            const r = activeCourse?.records.find(x => x.id === id);
            return r && r.trangThaiDuyet === 1;
        });

        approveMutation.mutate({
            diemDanhIds: recordsToApprove,
            action: 3 // Approved
        }, {
            onSuccess: () => {
                toast.success("Phê duyệt danh sách thành công và đã tích điểm CME!");
                setDetailSelectedIds([]);
                refetch();
                setOpenApproveModal(false);

                // If there are no more pending records in this course, close the detail modal
                const remainingPending = activeCourse?.records.filter(x => x.trangThaiDuyet === 1 && !recordsToApprove.includes(x.id)).length || 0;
                if (remainingPending === 0) {
                    setSelectedCourseId(null);
                }
            },
            onError: (err) => {
                toast.error(err?.message || "Lỗi khi phê duyệt.");
            }
        });
    };

    const confirmReject = () => {
        if (!lyDoTuChoi.trim()) {
            toast.error("Vui lòng nhập lý do từ chối.");
            return;
        }

        // Filter out status 3 ones
        const recordsToReject = detailSelectedIds.filter(id => {
            const r = activeCourse?.records.find(x => x.id === id);
            return r && r.trangThaiDuyet === 1;
        });

        approveMutation.mutate({
            diemDanhIds: recordsToReject,
            action: 2, // Rejected/Không hợp lệ
            lyDoTuChoi: lyDoTuChoi
        }, {
            onSuccess: () => {
                toast.success("Từ chối danh sách thành công.");
                setDetailSelectedIds([]);
                setLyDoTuChoi('');
                refetch();
                setOpenRejectModal(false);

                // If there are no more pending records in this course, close the detail modal
                const remainingPending = activeCourse?.records.filter(x => x.trangThaiDuyet === 1 && !recordsToReject.includes(x.id)).length || 0;
                if (remainingPending === 0) {
                    setSelectedCourseId(null);
                }
            },
            onError: (err) => {
                toast.error(err?.message || "Lỗi khi từ chối.");
            }
        });
    };

    return (
        <div className="container-fluid">
            <Card className="mb-4 shadow-sm border-0">
                <Card.Header className="bg-white border-bottom py-3">
                    <h5 className="mb-0 text-primary font-weight-bold">
                        <i className="fas fa-check-double mr-2"></i>Thẩm định & Phê duyệt CME Điều dưỡng (Bước 2)
                    </h5>
                </Card.Header>
                <Card.Body>
                    <div className="row mb-4 align-items-end">
                        <div className="col-md-4">
                            <Form.Label className="small font-weight-bold text-muted">Tìm kiếm tên khóa học</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Nhập tên khóa học..."
                                value={keyword}
                                onChange={onChangeKeyword}
                            />
                        </div>
                        <div className="col-md-3">
                            <Form.Label className="small font-weight-bold text-muted">Trạng thái duyệt</Form.Label>
                            <Form.Control as="select" value={statusFilter} onChange={onChangeStatus}>
                                <option value="">Tất cả</option>
                                <option value="1">Chờ duyệt Bước 2 (Đã qua B1)</option>
                                <option value="3">Đã duyệt (Tích điểm)</option>
                                <option value="2">Bị từ chối</option>
                            </Form.Control>
                        </div>
                    </div>

                    <div className="table-responsive" style={{ minHeight: '300px' }}>
                        <Table hover className="align-middle">
                            <thead className="bg-light text-muted text-uppercase" style={{ fontSize: '0.8rem' }}>
                                <tr>
                                    <th style={{ width: '50px' }}>STT</th>
                                    <th>Khóa đào tạo / Hoạt động</th>
                                    <th>Thời gian</th>
                                    <th className="text-center" style={{ width: '120px' }}>Số giờ CME</th>
                                    <th className="text-center" style={{ width: '120px' }}>Tổng số HV</th>
                                    <th className="text-center" style={{ width: '140px' }}>Chờ duyệt B2</th>
                                    <th className="text-center" style={{ width: '180px' }}>Đã duyệt / Từ chối</th>
                                    <th className="text-center" style={{ width: '150px' }}>Minh chứng</th>
                                    <th className="text-center" style={{ width: '150px' }}>Hành động</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan={9} className="text-center py-4">Đang tải dữ liệu...</td></tr>
                                ) : groupedCourses.length === 0 ? (
                                    <tr><td colSpan={9} className="text-center py-4 text-muted">Không tìm thấy khóa học nào có hồ sơ chờ phê duyệt.</td></tr>
                                ) : (
                                    groupedCourses.map((course, idx) => (
                                        <tr key={course.courseInfo.id}>
                                            <td>{(page - 1) * pageSize + idx + 1}</td>
                                            <td>
                                                <div className="font-weight-bold text-primary">{course.courseInfo.tenKhoaDaoTao}</div>
                                                {/* <small className="text-muted"><i className="fas fa-barcode mr-1"></i>Mã: {course.courseInfo.maKhoaDaoTao || 'N/A'}</small> */}
                                            </td>
                                            <td>
                                                <div className="small text-muted"><i className="far fa-calendar-alt mr-1"></i>Từ: {moment(course.courseInfo.tuNgay).format('DD/MM/YYYY')}</div>
                                                <div className="small text-muted"><i className="far fa-calendar-alt mr-1"></i>Đến: {moment(course.courseInfo.denNgay).format('DD/MM/YYYY')}</div>
                                            </td>
                                            <td className="text-center font-weight-bold text-success">{course.courseInfo.soGioTinChi}h</td>
                                            <td className="text-center font-weight-bold">{course.records.length}</td>
                                            <td className="text-center">
                                                {course.pendingCount > 0 ? (
                                                    <Badge variant="warning text-white" className="p-2">{course.pendingCount} Chờ duyệt</Badge>
                                                ) : (
                                                    <small className="text-muted">0</small>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                {course.approvedCount > 0 && <Badge variant="success" className="mr-1 p-2">{course.approvedCount} Đã duyệt</Badge>}
                                                {course.rejectedCount > 0 && <Badge variant="danger" className="p-2">{course.rejectedCount} Từ chối</Badge>}
                                                {course.approvedCount === 0 && course.rejectedCount === 0 && <small className="text-muted">-</small>}
                                            </td>
                                            <td className="text-center">
                                                {course.minhChung ? (
                                                    <a href={course.minhChung.startsWith('http') ? course.minhChung : `${BaseGatewayImage}${course.minhChung.startsWith('/') ? course.minhChung : '/' + course.minhChung}`} target="_blank" rel="noopener noreferrer" className="badge badge-info p-2">
                                                        <i className="fas fa-file-excel mr-1"></i>Tải file
                                                    </a>
                                                ) : (
                                                    <small className="text-muted">Không có</small>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedCourseId(course.courseInfo.id);
                                                        setDetailSelectedIds([]);
                                                    }}
                                                >
                                                    <i className="fas fa-tasks mr-1"></i>Xem & Duyệt
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <Form.Control as="select" value={pageSize} onChange={onChangeShowNumber} style={{ width: '80px' }}>
                            {objectShowNumber.map(opt => <option key={opt.value} value={opt.value}>{opt.text}</option>)}
                        </Form.Control>
                        <Pagination
                            activePage={page}
                            itemsCountPerPage={pageSize}
                            totalItemsCount={totalRecords}
                            pageRangeDisplayed={5}
                            onChange={changePage}
                            itemClass="page-item"
                            linkClass="page-link"
                        />
                    </div>
                </Card.Body>
            </Card>

            {/* Modal Chi tiết & Phê duyệt từng khóa học */}
            <Modal show={!!selectedCourseId} onHide={() => setSelectedCourseId(null)} size="xl" backdrop="static" style={{ zIndex: '9998' }}>
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title>
                        <i className="fas fa-users mr-2"></i>Danh sách học viên: {activeCourse?.courseInfo.tenKhoaDaoTao}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {activeCourse && (
                        <>
                            <div className="row mb-3 pb-3 border-bottom align-items-center">
                                <div className="col-md-5">
                                    <span className="mr-3"><strong>Thời gian:</strong> {moment(activeCourse.courseInfo.tuNgay).format('DD/MM/YYYY')} - {moment(activeCourse.courseInfo.denNgay).format('DD/MM/YYYY')}</span>
                                    <span className="mr-3"><strong>Số giờ CME:</strong> <span className="text-success font-weight-bold">{activeCourse.courseInfo.soGioTinChi}h</span></span>
                                    {activeCourse.minhChung && (
                                        <a
                                            href={activeCourse.minhChung.startsWith('http') ? activeCourse.minhChung : `${BaseGatewayImage}${activeCourse.minhChung.startsWith('/') ? activeCourse.minhChung : '/' + activeCourse.minhChung}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-outline-info btn-xs ml-2 py-1 px-2"
                                            style={{ fontSize: '0.75rem' }}
                                        >
                                            <i className="fas fa-file-excel mr-1"></i>Tải Minh chứng (Phụ lục 2)
                                        </a>
                                    )}
                                </div>
                                <div className="col-md-3">
                                    <Form.Control
                                        type="text"
                                        placeholder="Tìm kiếm học viên..."
                                        value={modalKeyword}
                                        onChange={(e) => setModalKeyword(e.target.value)}
                                        size="sm"
                                        style={{ borderRadius: '8px' }}
                                    />
                                </div>
                                <div className="col-md-4 text-right">
                                    <Button
                                        variant="success"
                                        size="sm"
                                        className="mr-2"
                                        disabled={detailSelectedIds.filter(id => {
                                            const r = activeCourse.records.find(x => x.id === id);
                                            return r && r.trangThaiDuyet === 1;
                                        }).length === 0 || approveMutation.isLoading}
                                        onClick={() => setOpenApproveModal(true)}
                                    >
                                        Duyệt Hợp lệ Bước 2 ({detailSelectedIds.filter(id => {
                                            const r = activeCourse.records.find(x => x.id === id);
                                            return r && r.trangThaiDuyet === 1;
                                        }).length})
                                    </Button>
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        disabled={detailSelectedIds.filter(id => {
                                            const r = activeCourse.records.find(x => x.id === id);
                                            return r && r.trangThaiDuyet === 1;
                                        }).length === 0 || approveMutation.isLoading}
                                        onClick={() => setOpenRejectModal(true)}
                                    >
                                        Từ chối ({detailSelectedIds.filter(id => {
                                            const r = activeCourse.records.find(x => x.id === id);
                                            return r && r.trangThaiDuyet === 1;
                                        }).length})
                                    </Button>
                                </div>
                            </div>

                            <div className="table-responsive" style={{ maxHeight: '480px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                                <Table hover className="align-middle mb-0">
                                    <thead className="text-muted text-uppercase" style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1, fontSize: '0.8rem', borderBottom: '2px solid #dee2e6' }}>
                                        <tr>
                                            <th className="text-center" style={{ width: '40px' }}>
                                                <Form.Check
                                                    type="checkbox"
                                                    onChange={(e) => handleSelectAllDetail(e.target.checked)}
                                                    checked={filteredModalRecords.filter(x => x.trangThaiDuyet === 1).length > 0 && filteredModalRecords.filter(x => x.trangThaiDuyet === 1).every(x => detailSelectedIds.includes(x.id))}
                                                />
                                            </th>
                                            <th style={{ width: '50px' }}>STT</th>
                                            <th>CCCD / Mã NV</th>
                                            <th>Cán bộ Điều dưỡng</th>
                                            <th>Đơn vị / Khoa</th>
                                            <th className="text-center">Giờ Điểm danh Đến</th>
                                            <th className="text-center">Giờ Điểm danh Về</th>
                                            <th className="text-center" style={{ width: '150px' }}>Trạng thái</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredModalRecords.length === 0 ? (
                                            <tr><td colSpan={8} className="text-center py-4 text-muted">Không tìm thấy học viên nào phù hợp.</td></tr>
                                        ) : (
                                            filteredModalRecords.map((item, idx) => {
                                                return (
                                                    <tr key={item.id}>
                                                        <td className="text-center">
                                                            {item.trangThaiDuyet === 1 ? (
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    checked={detailSelectedIds.includes(item.id)}
                                                                    onChange={(e) => handleSelectOneDetail(item.id, e.target.checked)}
                                                                />
                                                            ) : item.trangThaiDuyet === 3 ? (
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    checked={true}
                                                                    disabled={true}
                                                                />
                                                            ) : item.trangThaiDuyet === 2 ? (
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    checked={false}
                                                                    disabled={true}
                                                                />
                                                            ) : null}
                                                        </td>
                                                        <td>{idx + 1}</td>
                                                        <td>
                                                            <CMEParticipantCell userId={item.userId} mode="username" />
                                                        </td>
                                                        <td>
                                                            <UserInfoCell userId={item.userId} hideUsername={true} />
                                                        </td>
                                                        <td>
                                                            {unitMap[item.userId]?.code || '-'}
                                                        </td>
                                                        <td className="text-center">
                                                            {item.thoiGianDen ? moment(item.thoiGianDen).format('HH:mm DD/MM/YYYY') : 'N/A'}
                                                        </td>
                                                        <td className="text-center">
                                                            {item.thoiGianVe ? moment(item.thoiGianVe).format('HH:mm DD/MM/YYYY') : 'N/A'}
                                                        </td>
                                                        <td className="text-center">
                                                            {item.trangThaiDuyet === 1 && <Badge variant="warning text-white">Chờ HLĐT duyệt (B2)</Badge>}
                                                            {item.trangThaiDuyet === 2 && (
                                                                <div>
                                                                    <Badge variant="danger">Bị từ chối</Badge>
                                                                    {item.lyDoTuChoi && <div className="text-danger small mt-1 italic">{item.lyDoTuChoi}</div>}
                                                                </div>
                                                            )}
                                                            {item.trangThaiDuyet === 3 && <Badge variant="success">Đã duyệt & Tích điểm</Badge>}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setSelectedCourseId(null)}>Đóng</Button>
                </Modal.Footer>
            </Modal>

            <ConfirmModal
                isOpen={openApproveModal}
                confirmType="success"
                handleAccept={confirmApprove}
                handleCancel={() => setOpenApproveModal(false)}
                title="Xác nhận phê duyệt"
                content={`Bạn có chắc chắn muốn phê duyệt ${detailSelectedIds.length} bản ghi đã chọn? Hành động này sẽ tự động tích lũy điểm CME cho học viên.`}
            />

            <Modal show={openRejectModal} onHide={() => setOpenRejectModal(false)} backdrop="static" style={{ zIndex: '9999' }}>
                <Modal.Header closeButton className="bg-danger text-white">
                    <Modal.Title>Từ chối hồ sơ CME Điều dưỡng</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label className="font-weight-bold">Lý do từ chối <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Nhập lý do từ chối phê duyệt..."
                            value={lyDoTuChoi}
                            onChange={(e) => setLyDoTuChoi(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setOpenRejectModal(false)}>Hủy</Button>
                    <Button variant="danger" onClick={confirmReject}>Từ chối</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default CMEDieuDuongDuyet;
